import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useShallow } from "zustand/shallow";

import { storage } from "@/shared/utils/local-storage";
import { FetchedTag } from "@/services/api/fetchers/tags";
import { useBoundStore } from "@/app/store";
import { useOpenState } from "@/shared/hooks/useOpenState";

export function useNotesState(currentFolderId: number | null) {
  const [selectedNotes, setSelectedNotes] = useState<Set<number>>(new Set());
  const [selectedFolders, setSelectedFolders] = useState<Set<number>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  const storedSearch = storage.getNotesSearch();
  const { isOpen: isSelectionMode, handleOpenChange: setIsSelectionMode } = useOpenState({ id: "notes-selection" });

  // Initialize from URL params first, then fall back to stored search
  const urlTagIds = searchParams.get("tags")?.split(",").filter(Boolean) || [];
  const initialTagIds = urlTagIds.length > 0 ? urlTagIds : storedSearch.tags.map((tag) => tag.id.toString());

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(initialTagIds);
  const [selectedTags, setSelectedTags] = useState<FetchedTag[]>([]);

  const { notes, tags, addNote, tempDeleteNote, updateNote } = useBoundStore(
    useShallow((state) => ({
      notes: state.notes.main,
      tags: state.tags.list,
      addNote: state.notes.addNote,
      tempDeleteNote: state.notes.tempDeleteNote,
      updateNote: state.notes.updateNote,
    }))
  );

  const hasNotes = Boolean(notes?.length);

  // Clear selection when exiting selection mode used when back button is pressed
  useEffect(() => {
    if (!isSelectionMode) {
      setSelectedNotes(new Set());
      setSelectedFolders(new Set());
    }
  }, [isSelectionMode]);

  // Initialize selectedTags from selectedTagIds once tags are loaded
  useEffect(() => {
    if (tags.length > 0 && selectedTagIds.length > 0) {
      const tagsFromIds = tags.filter((tag) => selectedTagIds.includes(tag.id.toString()));
      setSelectedTags(tagsFromIds);
    } else if (selectedTagIds.length === 0) {
      setSelectedTags([]);
    }
  }, [tags, selectedTagIds]);

  // Initialize URL params from stored search on mount
  useEffect(() => {
    // Check if URL already has parameters (user navigated with specific state)
    const hasUrlParams = searchParams.get("q") || searchParams.get("tags");

    // Only restore from localStorage if there are no URL parameters
    if (!hasUrlParams) {
      const newParams = new URLSearchParams(searchParams);
      let hasChanges = false;

      // Handle search text
      if (storedSearch.text) {
        newParams.set("q", storedSearch.text);
        hasChanges = true;
      }

      // Handle tags from stored search
      if (storedSearch.tags.length > 0) {
        const tagIds = storedSearch.tags.map((tag) => tag.id.toString()).join(",");
        newParams.set("tags", tagIds);
        hasChanges = true;
      }

      if (hasChanges) {
        setSearchParams(newParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Update URL and localStorage when selectedTags changes
  useEffect(() => {
    const newTagIds = selectedTags.map((tag) => tag.id.toString());
    const currentUrlTagIds = searchParams.get("tags")?.split(",").filter(Boolean) || [];

    // Only update if the URL tags don't match the current selected tags
    if (JSON.stringify(newTagIds.sort()) !== JSON.stringify(currentUrlTagIds.sort())) {
      const newParams = new URLSearchParams(searchParams);
      if (newTagIds.length > 0) {
        newParams.set("tags", newTagIds.join(","));
      } else {
        newParams.delete("tags");
      }
      setSearchParams(newParams, { replace: true });

      // Update local storage with current search text and selected tags
      const currentSearchText = searchParams.get("q") || "";
      storage.setNotesSearch({ text: currentSearchText, tags: selectedTags });
    }
  }, [selectedTags, searchParams, setSearchParams]);

  // Sync localStorage with URL parameters to handle external URL changes
  useEffect(() => {
    const currentSearchText = searchParams.get("q") || "";
    const currentUrlTagIds = searchParams.get("tags")?.split(",").filter(Boolean) || [];
    const currentUrlTags = tags.filter((tag) => currentUrlTagIds.includes(tag.id.toString()));

    // Update localStorage to match URL state (important for navigation and external links)
    storage.setNotesSearch({ text: currentSearchText, tags: currentUrlTags });
  }, [searchParams, tags]);

  const filteredNotes = useMemo(() => {
    if (!notes) return [];
    const query = searchParams.get("q")?.toLowerCase();

    return notes.filter((note) => {
      // Text search
      const matchesSearch = !query || note.text_content?.toLowerCase().includes(query);

      // Tag filtering
      const matchesTags =
        selectedTags.length === 0 || selectedTags.every((tag) => note.tags.some((noteTag) => noteTag.id === tag.id));

      // Folder filtering
      const matchesFolder = note.folder_id === currentFolderId;

      return matchesSearch && matchesTags && matchesFolder;
    });
  }, [notes, searchParams, selectedTags, currentFolderId]);

  const handleTagsFilterChange = useCallback(
    (newSelectedTagIds: string[]) => {
      const newSelectedTags = tags.filter((tag) => newSelectedTagIds.includes(tag.id.toString()));

      setSelectedTagIds(newSelectedTagIds);
      setSelectedTags(newSelectedTags);

      // Update URL parameters
      const newParams = new URLSearchParams(searchParams);
      if (newSelectedTagIds.length > 0) {
        newParams.set("tags", newSelectedTagIds.join(","));
      } else {
        newParams.delete("tags");
      }
      setSearchParams(newParams, { replace: true });

      // Update local storage with current search text and new selected tags
      const currentSearchText = searchParams.get("q") || "";
      storage.setNotesSearch({ text: currentSearchText, tags: newSelectedTags });
    },
    [tags, searchParams, setSearchParams]
  );

  const clearSelection = useCallback(() => {
    setSelectedNotes(new Set());
    setSelectedFolders(new Set());
    setIsSelectionMode(false);
  }, [setIsSelectionMode]);

  return {
    // State
    selectedNotes,
    setSelectedNotes,
    selectedFolders,
    setSelectedFolders,
    isSelectionMode,
    setIsSelectionMode,
    selectedTagIds,
    setSelectedTagIds,
    selectedTags,
    setSelectedTags,
    searchParams,
    setSearchParams,

    // Store data
    notes,
    tags,
    addNote,
    tempDeleteNote,
    updateNote,
    hasNotes,
    filteredNotes,

    // Handlers
    handleTagsFilterChange,
    clearSelection,
  };
}
